import { supabase } from '../lib/supabase';

/**
 * Unified Contact Management Service
 * Handles all database operations for contacts (leads, prospects, customers, partners, vendors)
 * Converts between snake_case (database) and camelCase (React)
 */

export const contactService = {
  /**
   * Get all contacts with optional type filter
   * @param {string|null} contactType - Filter by contact type (lead, prospect, customer, partner, vendor)
   * @param {string|null} tenantId - Tenant ID for filtering
   * @param {boolean} isSuperAdmin - Whether user is Super Admin
   * @returns {Promise<Array>} Array of contact objects
   */
  async getAllContacts(contactType = null, tenantId = null, isSuperAdmin = false) {
    try {
      let query = supabase?.from('contacts')?.select(`
          *,
          created_by_user:user_profiles!created_by(first_name, last_name, email),
          assigned_user:user_profiles!assigned_user_id(first_name, last_name, email),
          primary_contact:contacts!primary_contact_id(first_name, last_name, email)
        `)?.order('created_at', { ascending: false });

      // Apply tenant filter ONLY for non-Super Admins
      if (!isSuperAdmin && tenantId) {
        query = query?.eq('tenant_id', tenantId);
      }

      // Apply contact type filter if specified
      if (contactType) {
        query = query?.eq('contact_type', contactType);
      }

      const { data, error } = await query;

      if (error) throw error;

      return data?.map(contact => ({
        id: contact?.id,
        tenantId: contact?.tenant_id,
        firstName: contact?.first_name,
        lastName: contact?.last_name,
        fullName: `${contact?.first_name} ${contact?.last_name}`,
        email: contact?.email,
        phone: contact?.phone,
        secondaryPhone: contact?.secondary_phone,
        company: contact?.company,
        companyName: contact?.company_name,
        jobTitle: contact?.job_title,
        contactType: contact?.contact_type,
        status: contact?.status,
        assignedUserId: contact?.assigned_user_id,
        assignedUser: contact?.assigned_user,
        // Address fields
        address: contact?.address,
        streetAddress: contact?.street_address,
        city: contact?.city,
        state: contact?.state,
        zipCode: contact?.zip_code,
        country: contact?.country,
        // Social
        linkedinUrl: contact?.linkedin_url,
        twitterUrl: contact?.twitter_url,
        notes: contact?.notes,
        tags: contact?.tags || [],
        isActive: contact?.is_active,
        lastContactedAt: contact?.last_contacted_at,
        lastActivity: contact?.last_activity,
        // Customer/Personal fields
        householdType: contact?.household_type,
        homeownerStatus: contact?.homeowner_status,
        spousePartnerName: contact?.spouse_partner_name,
        squareFootage: contact?.square_footage,
        yearBuilt: contact?.year_built,
        gateCode: contact?.gate_code,
        accessInstructions: contact?.access_instructions,
        hasPets: contact?.has_pets,
        petNotes: contact?.pet_notes,
        // Business fields
        businessName: contact?.business_name,
        mainPhone: contact?.main_phone,
        website: contact?.website,
        primaryContactId: contact?.primary_contact_id,
        primaryContact: contact?.primary_contact,
        secondaryContactIds: contact?.secondary_contact_ids || [],
        // Service delivery fields
        industry: contact?.industry,
        subscriptionStatus: contact?.subscription_status,
        annualRevenue: contact?.annual_revenue,
        communicationPreference: contact?.communication_preference,
        registrationDate: contact?.registration_date,
        createdBy: contact?.created_by_user,
        createdAt: contact?.created_at,
        updatedAt: contact?.updated_at
      })) || [];
    } catch (error) {
      console.error('Error fetching contacts:', error);
      throw error;
    }
  },

  /**
   * Get customers only (contact_type = 'customer')
   */
  async getCustomers(tenantId = null, isSuperAdmin = false) {
    return this.getAllContacts('customer', tenantId, isSuperAdmin);
  },

  /**
   * Get leads only (contact_type = 'lead')
   */
  async getLeads(tenantId = null, isSuperAdmin = false) {
    return this.getAllContacts('lead', tenantId, isSuperAdmin);
  },

  /**
   * Get prospects only (contact_type = 'prospect')
   */
  async getProspects(tenantId = null, isSuperAdmin = false) {
    return this.getAllContacts('prospect', tenantId, isSuperAdmin);
  },

  /**
   * Get single contact by ID with full details
   */
  async getById(contactId) {
    try {
      const { data, error } = await supabase?.from('contacts')?.select(`
          *,
          assigned_user:user_profiles!assigned_user_id(first_name, last_name, email),
          primary_contact:contacts!primary_contact_id(first_name, last_name, email, phone),
          contact_addresses(*),
          contact_tags(*),
          contact_notes(*, created_by_profile:user_profiles!contact_notes_created_by_fkey(first_name, last_name)),
          contact_properties(*)
        `)?.eq('id', contactId)?.single();

      if (error) throw error;

      // Convert to camelCase
      return {
        id: data?.id,
        tenantId: data?.tenant_id,
        createdBy: data?.created_by,
        firstName: data?.first_name,
        lastName: data?.last_name,
        fullName: `${data?.first_name} ${data?.last_name}`,
        email: data?.email,
        phone: data?.phone,
        secondaryPhone: data?.secondary_phone,
        company: data?.company,
        companyName: data?.company_name,
        jobTitle: data?.job_title,
        contactType: data?.contact_type,
        status: data?.status,
        assignedUserId: data?.assigned_user_id,
        assignedUser: data?.assigned_user,
        // Address
        address: data?.address,
        streetAddress: data?.street_address,
        city: data?.city,
        state: data?.state,
        zipCode: data?.zip_code,
        country: data?.country,
        // Social
        linkedinUrl: data?.linkedin_url,
        twitterUrl: data?.twitter_url,
        notes: data?.notes,
        tags: data?.tags || [],
        isActive: data?.is_active,
        lastContactedAt: data?.last_contacted_at,
        lastActivity: data?.last_activity,
        // Personal/Customer fields
        householdType: data?.household_type,
        homeownerStatus: data?.homeowner_status,
        spousePartnerName: data?.spouse_partner_name,
        squareFootage: data?.square_footage,
        yearBuilt: data?.year_built,
        gateCode: data?.gate_code,
        accessInstructions: data?.access_instructions,
        hasPets: data?.has_pets,
        petNotes: data?.pet_notes,
        // Business fields
        businessName: data?.business_name,
        mainPhone: data?.main_phone,
        website: data?.website,
        primaryContactId: data?.primary_contact_id,
        primaryContact: data?.primary_contact,
        secondaryContactIds: data?.secondary_contact_ids || [],
        // Service delivery
        industry: data?.industry,
        subscriptionStatus: data?.subscription_status,
        annualRevenue: data?.annual_revenue,
        communicationPreference: data?.communication_preference,
        registrationDate: data?.registration_date,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at,
        addresses: data?.contact_addresses?.map(addr => ({
          id: addr?.id,
          contactId: addr?.contact_id,
          addressType: addr?.address_type,
          street: addr?.street,
          city: addr?.city,
          state: addr?.state,
          zipCode: addr?.zip_code,
          country: addr?.country,
          latitude: addr?.latitude,
          longitude: addr?.longitude,
          isPrimary: addr?.is_primary,
          notes: addr?.notes
        })) || [],
        contactTags: data?.contact_tags?.map(tag => ({
          id: tag?.id,
          contactId: tag?.contact_id,
          tagName: tag?.tag_name,
          createdAt: tag?.created_at
        })) || [],
        contactNotes: data?.contact_notes?.map(note => ({
          id: note?.id,
          contactId: note?.contact_id,
          createdBy: note?.created_by,
          noteType: note?.note_type,
          content: note?.content,
          voiceUrl: note?.voice_url,
          isPinned: note?.is_pinned,
          createdAt: note?.created_at,
          updatedAt: note?.updated_at,
          createdByName: note?.created_by_profile ? 
            `${note?.created_by_profile?.first_name} ${note?.created_by_profile?.last_name}` : 
            'Unknown'
        })) || [],
        properties: data?.contact_properties?.map(prop => ({
          id: prop?.id,
          contactId: prop?.contact_id,
          addressId: prop?.address_id,
          uploadedBy: prop?.uploaded_by,
          photoUrl: prop?.photo_url,
          caption: prop?.caption,
          photoType: prop?.photo_type,
          takenAt: prop?.taken_at,
          createdAt: prop?.created_at
        })) || []
      };
    } catch (error) {
      console.error('Error fetching contact details:', error);
      throw error;
    }
  },

  /**
   * Create new contact
   * @param {Object} contactData - Contact data to create
   * @returns {Promise<Object>} Created contact object
   */
  async createContact(contactData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get user's tenant_id
      const { data: profile } = await supabase?.from('user_profiles')?.select('tenant_id')?.eq('id', user?.id)?.single();

      const { data, error } = await supabase?.from('contacts')?.insert({
          tenant_id: profile?.tenant_id,
          created_by: user?.id,
          first_name: contactData?.firstName,
          last_name: contactData?.lastName,
          email: contactData?.email,
          phone: contactData?.phone,
          secondary_phone: contactData?.secondaryPhone,
          company: contactData?.company,
          company_name: contactData?.companyName,
          job_title: contactData?.jobTitle,
          contact_type: contactData?.contactType || 'lead',
          status: contactData?.status || 'lead',
          assigned_user_id: contactData?.assignedUserId || null,
          // Address
          address: contactData?.address,
          street_address: contactData?.streetAddress,
          city: contactData?.city,
          state: contactData?.state,
          zip_code: contactData?.zipCode,
          country: contactData?.country || 'USA',
          // Social
          linkedin_url: contactData?.linkedinUrl,
          twitter_url: contactData?.twitterUrl,
          notes: contactData?.notes,
          tags: contactData?.tags || [],
          // Personal/Customer fields - convert empty strings to null for enums
          household_type: contactData?.householdType || null,
          homeowner_status: contactData?.homeownerStatus || null,
          spouse_partner_name: contactData?.spousePartnerName,
          square_footage: contactData?.squareFootage === '' ? null : contactData?.squareFootage,
          year_built: contactData?.yearBuilt === '' ? null : contactData?.yearBuilt,
          gate_code: contactData?.gateCode,
          access_instructions: contactData?.accessInstructions,
          has_pets: contactData?.hasPets || false,
          pet_notes: contactData?.petNotes,
          // Business fields
          business_name: contactData?.businessName,
          main_phone: contactData?.mainPhone,
          website: contactData?.website,
          primary_contact_id: contactData?.primaryContactId || null,
          secondary_contact_ids: contactData?.secondaryContactIds || [],
          // Service delivery - convert empty string to null for enum
          industry: contactData?.industry === '' ? null : contactData?.industry,
          subscription_status: contactData?.subscriptionStatus || 'inactive',
          annual_revenue: contactData?.annualRevenue === '' ? null : contactData?.annualRevenue,
          communication_preference: contactData?.communicationPreference || 'email'
        })?.select()?.single();

      if (error) throw error;

      return {
        id: data?.id,
        tenantId: data?.tenant_id,
        createdBy: data?.created_by,
        firstName: data?.first_name,
        lastName: data?.last_name,
        fullName: `${data?.first_name} ${data?.last_name}`,
        email: data?.email,
        phone: data?.phone,
        secondaryPhone: data?.secondary_phone,
        company: data?.company,
        companyName: data?.company_name,
        jobTitle: data?.job_title,
        contactType: data?.contact_type,
        status: data?.status,
        assignedUserId: data?.assigned_user_id,
        assignedUser: data?.assigned_user,
        // Address
        address: data?.address,
        streetAddress: data?.street_address,
        city: data?.city,
        state: data?.state,
        zipCode: data?.zip_code,
        country: data?.country,
        // Social
        linkedinUrl: data?.linkedin_url,
        twitterUrl: data?.twitter_url,
        notes: data?.notes,
        tags: data?.tags || [],
        isActive: data?.is_active,
        lastContactedAt: data?.last_contacted_at,
        lastActivity: data?.last_activity,
        // Personal/Customer fields
        householdType: data?.household_type,
        homeownerStatus: data?.homeowner_status,
        spousePartnerName: data?.spouse_partner_name,
        squareFootage: data?.square_footage,
        yearBuilt: data?.year_built,
        gateCode: data?.gate_code,
        accessInstructions: data?.access_instructions,
        hasPets: data?.has_pets,
        petNotes: data?.pet_notes,
        // Business fields
        businessName: data?.business_name,
        mainPhone: data?.main_phone,
        website: data?.website,
        primaryContactId: data?.primary_contact_id,
        primaryContact: data?.primary_contact,
        secondaryContactIds: data?.secondary_contact_ids || [],
        // Service delivery
        industry: data?.industry,
        subscriptionStatus: data?.subscription_status,
        annualRevenue: data?.annual_revenue,
        communicationPreference: data?.communication_preference,
        registrationDate: data?.registration_date,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error creating contact:', error);
      throw error;
    }
  },

  /**
   * Update existing contact
   * @param {string} contactId - Contact ID to update
   * @param {Object} contactData - Updated contact data
   * @returns {Promise<Object>} Updated contact object
   */
  async updateContact(contactId, contactData) {
    try {
      const { data, error } = await supabase?.from('contacts')?.update({
          first_name: contactData?.firstName,
          last_name: contactData?.lastName,
          email: contactData?.email,
          phone: contactData?.phone,
          secondary_phone: contactData?.secondaryPhone,
          company: contactData?.company,
          company_name: contactData?.companyName,
          job_title: contactData?.jobTitle,
          contact_type: contactData?.contactType,
          status: contactData?.status,
          assigned_user_id: contactData?.assignedUserId || null,
          // Address
          address: contactData?.address,
          street_address: contactData?.streetAddress,
          city: contactData?.city,
          state: contactData?.state,
          zip_code: contactData?.zipCode,
          country: contactData?.country,
          // Social
          linkedin_url: contactData?.linkedinUrl,
          twitter_url: contactData?.twitterUrl,
          notes: contactData?.notes,
          tags: contactData?.tags,
          // Personal/Customer fields - convert empty strings to null for enums
          household_type: contactData?.householdType === '' ? null : contactData?.householdType,
          homeowner_status: contactData?.homeownerStatus === '' ? null : contactData?.homeownerStatus,
          spouse_partner_name: contactData?.spousePartnerName,
          square_footage: contactData?.squareFootage || null,
          year_built: contactData?.yearBuilt || null,
          gate_code: contactData?.gateCode,
          access_instructions: contactData?.accessInstructions,
          has_pets: contactData?.hasPets,
          pet_notes: contactData?.petNotes,
          // Business fields
          business_name: contactData?.businessName,
          main_phone: contactData?.mainPhone,
          website: contactData?.website,
          primary_contact_id: contactData?.primaryContactId || null,
          secondary_contact_ids: contactData?.secondaryContactIds,
          // Service delivery - convert empty string to null for enum
          industry: contactData?.industry || null,
          subscription_status: contactData?.subscriptionStatus,
          annual_revenue: contactData?.annualRevenue || null,
          communication_preference: contactData?.communicationPreference,
          updated_at: new Date()?.toISOString()
        })?.eq('id', contactId)?.select()?.single();

      if (error) throw error;

      return {
        id: data?.id,
        firstName: data?.first_name,
        lastName: data?.last_name,
        fullName: `${data?.first_name} ${data?.last_name}`,
        email: data?.email,
        phone: data?.phone,
        secondaryPhone: data?.secondary_phone,
        contactType: data?.contact_type,
        status: data?.status,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error updating contact:', error);
      throw error;
    }
  },

  /**
   * Get customer contacts only (for Primary Contact dropdown in business forms)
   * @param {string|null} tenantId - Tenant ID for filtering
   * @param {boolean} isSuperAdmin - Whether user is Super Admin
   * @returns {Promise<Array>} Array of customer contact objects
   */
  async getCustomerContactsForDropdown(tenantId = null, isSuperAdmin = false) {
    try {
      let query = supabase?.from('contacts')?.select('id, first_name, last_name, email, phone')?.eq('contact_type', 'customer')?.order('first_name', { ascending: true });

      if (!isSuperAdmin && tenantId) {
        query = query?.eq('tenant_id', tenantId);
      }

      const { data, error } = await query;

      if (error) throw error;

      return data?.map(contact => ({
        id: contact?.id,
        firstName: contact?.first_name,
        lastName: contact?.last_name,
        fullName: `${contact?.first_name} ${contact?.last_name}`,
        email: contact?.email,
        phone: contact?.phone
      })) || [];
    } catch (error) {
      console.error('Error fetching customer contacts:', error);
      throw error;
    }
  },

  /**
   * Get all users for Assigned User dropdown
   * @param {string|null} tenantId - Tenant ID for filtering
   * @returns {Promise<Array>} Array of user objects
   */
  async getUsersForDropdown(tenantId = null) {
    try {
      let query = supabase?.from('user_profiles')?.select('id, first_name, last_name, email')?.eq('is_active', true)?.order('first_name', { ascending: true });

      if (tenantId) {
        query = query?.eq('tenant_id', tenantId);
      }

      const { data, error } = await query;

      if (error) throw error;

      return data?.map(user => ({
        id: user?.id,
        firstName: user?.first_name,
        lastName: user?.last_name,
        fullName: `${user?.first_name} ${user?.last_name}`,
        email: user?.email
      })) || [];
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  },

  /**
   * Delete contact
   * @param {string} contactId - Contact ID to delete
   * @returns {Promise<void>}
   */
  async deleteContact(contactId) {
    try {
      const { error } = await supabase?.from('contacts')?.delete()?.eq('id', contactId);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting contact:', error);
      throw error;
    }
  },

  /**
   * Bulk delete contacts
   * @param {Array<string>} contactIds - Array of contact IDs to delete
   * @returns {Promise<void>}
   */
  async bulkDeleteContacts(contactIds) {
    try {
      const { error } = await supabase?.from('contacts')?.delete()?.in('id', contactIds);

      if (error) throw error;
    } catch (error) {
      console.error('Error bulk deleting contacts:', error);
      throw error;
    }
  },

  /**
   * Manually promote contact to customer
   * @param {string} contactId - Contact ID to promote
   * @returns {Promise<Object>} Updated contact
   */
  async promoteToCustomer(contactId) {
    try {
      const { data, error } = await supabase?.from('contacts')?.update({
          contact_type: 'customer',
          subscription_status: 'active'
        })?.eq('id', contactId)?.select()?.single();

      if (error) throw error;

      return {
        id: data?.id,
        contactType: data?.contact_type,
        subscriptionStatus: data?.subscription_status
      };
    } catch (error) {
      console.error('Error promoting contact to customer:', error);
      throw error;
    }
  },

  /**
   * Add address to contact
   */
  async addAddress(contactId, addressData) {
    try {
      const { data, error } = await supabase?.from('contact_addresses')?.insert({
          contact_id: contactId,
          address_type: addressData?.addressType || 'service',
          street: addressData?.street,
          city: addressData?.city,
          state: addressData?.state,
          zip_code: addressData?.zipCode,
          country: addressData?.country || 'USA',
          latitude: addressData?.latitude,
          longitude: addressData?.longitude,
          is_primary: addressData?.isPrimary || false,
          notes: addressData?.notes
        })?.select()?.single();

      if (error) throw error;

      return {
        id: data?.id,
        contactId: data?.contact_id,
        street: data?.street,
        city: data?.city,
        state: data?.state,
        zipCode: data?.zip_code,
        isPrimary: data?.is_primary
      };
    } catch (error) {
      console.error('Error adding address:', error);
      throw error;
    }
  },

  /**
   * Add tag to contact
   */
  async addTag(contactId, tagName) {
    try {
      const { data, error } = await supabase?.from('contact_tags')?.insert({
          contact_id: contactId,
          tag_name: tagName
        })?.select()?.single();

      if (error) throw error;

      return {
        id: data?.id,
        contactId: data?.contact_id,
        tagName: data?.tag_name,
        createdAt: data?.created_at
      };
    } catch (error) {
      console.error('Error adding tag:', error);
      throw error;
    }
  },

  /**
   * Remove tag from contact
   */
  async removeTag(tagId) {
    try {
      const { error } = await supabase?.from('contact_tags')?.delete()?.eq('id', tagId);

      if (error) throw error;
    } catch (error) {
      console.error('Error removing tag:', error);
      throw error;
    }
  },

  /**
   * Add note to contact
   */
  async addNote(contactId, noteData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('contact_notes')?.insert({
          contact_id: contactId,
          created_by: user?.id,
          note_type: noteData?.noteType || 'general',
          content: noteData?.content,
          voice_url: noteData?.voiceUrl,
          is_pinned: noteData?.isPinned || false
        })?.select()?.single();

      if (error) throw error;

      return {
        id: data?.id,
        contactId: data?.contact_id,
        noteType: data?.note_type,
        content: data?.content,
        isPinned: data?.is_pinned,
        createdAt: data?.created_at
      };
    } catch (error) {
      console.error('Error adding note:', error);
      throw error;
    }
  },

  /**
   * Update note
   */
  async updateNote(noteId, noteData) {
    try {
      const { data, error } = await supabase?.from('contact_notes')?.update({
          content: noteData?.content,
          is_pinned: noteData?.isPinned
        })?.eq('id', noteId)?.select()?.single();

      if (error) throw error;

      return {
        id: data?.id,
        content: data?.content,
        isPinned: data?.is_pinned,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error updating note:', error);
      throw error;
    }
  },

  /**
   * Delete note
   */
  async deleteNote(noteId) {
    try {
      const { error } = await supabase?.from('contact_notes')?.delete()?.eq('id', noteId);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting note:', error);
      throw error;
    }
  },

  /**
   * Bulk update contacts
   */
  async bulkUpdate(contactIds, updateData) {
    try {
      const updates = {};
      
      if (updateData?.contactType) {
        updates.contact_type = updateData?.contactType;
      }
      if (updateData?.subscriptionStatus) {
        updates.subscription_status = updateData?.subscriptionStatus;
      }
      if (updateData?.isActive !== undefined) {
        updates.is_active = updateData?.isActive;
      }

      const { error } = await supabase?.from('contacts')?.update(updates)?.in('id', contactIds);

      if (error) throw error;
    } catch (error) {
      console.error('Error bulk updating contacts:', error);
      throw error;
    }
  },

  // Legacy compatibility methods (for components still using old customer service)
  async getAll(tenantId = null, isSuperAdmin = false) {
    return this.getCustomers(tenantId, isSuperAdmin);
  },

  async create(customerData) {
    return this.createContact({
      firstName: customerData?.contactPerson?.split(' ')?.[0] || 'Unknown',
      lastName: customerData?.contactPerson?.split(' ')?.slice(1)?.join(' ') || 'Contact',
      companyName: customerData?.companyName,
      email: customerData?.email,
      phone: customerData?.phone,
      contactType: 'customer',
      industry: customerData?.industry,
      subscriptionStatus: customerData?.subscriptionStatus,
      annualRevenue: customerData?.annualRevenue,
      communicationPreference: customerData?.communicationPreference,
      notes: customerData?.notes
    });
  },

  async update(contactId, customerData) {
    return this.updateContact(contactId, {
      firstName: customerData?.contactPerson?.split(' ')?.[0],
      lastName: customerData?.contactPerson?.split(' ')?.slice(1)?.join(' '),
      companyName: customerData?.companyName,
      email: customerData?.email,
      phone: customerData?.phone,
      industry: customerData?.industry,
      subscriptionStatus: customerData?.subscriptionStatus,
      annualRevenue: customerData?.annualRevenue,
      communicationPreference: customerData?.communicationPreference,
      lastContactedAt: customerData?.lastContactDate,
      isActive: customerData?.isActive,
      notes: customerData?.notes
    });
  },

  async delete(contactId) {
    return this.deleteContact(contactId);
  }
};